import { Grid, styled, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isRtl } from '../../../utils/booking';
import isValidUsername from '../../../utils/validations/userNameValidation';

interface IStyledTextFieldProps {
  isRTL: boolean;
  error?: boolean;
}

const StyledTextField = styled(TextField)<IStyledTextFieldProps>(
  ({ theme, isRTL, error = false }) => ({
    '& .MuiInputBase-root': {
      backgroundColor: error ? '#E161621A' : '#FFFFFF',
      borderRadius: isRTL ? '10px 0 0 10px' : '0 10px 10px 0',
      padding: '8px 12px !important',
      height: '48px',
    },
    '& .MuiInputBase-input': {
      ...theme.typography.bodyBold,
      color: error ? '#E16162' : theme.palette.primary.main,
      '&:-webkit-autofill': {
        '-webkit-text-fill-color': theme.palette.primary.main,
        '-webkit-box-shadow': `0 0 0 1000px ${theme.palette.primary.contrastText} inset`,
      },
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        border: `1px solid rgba(124, 174, 190, 0.50)`,
      },
      '&:hover fieldset': {
        border: `1px solid rgba(124, 174, 190, 0.50)`,
      },
      '&.Mui-focused fieldset': {
        border: `1px solid rgba(124, 174, 190, 0.50)`,
      },
    },
  })
);

type IsetURLStepProps = {
  url: string;
  setUrl: (_url: string) => void;
  urlExistStatus: null | boolean;
  urlExistError?: string | null;
  hasPermissions: boolean;
};

function SetURLStep({
  url,
  setUrl,
  urlExistStatus,
  urlExistError,
  hasPermissions,
}: IsetURLStepProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const getError = () => {
    if (urlExistStatus === false) {
      if (urlExistError) return urlExistError;

      return t('bookings.setURLStep.urlAlreadyExists');
    }
    const validation = isValidUsername(url, t);

    if (!validation.isValid) {
      return validation.message;
    }
  };

  return (
    <Grid display="flex" flexDirection="column" alignItems="center" height="calc(100% - 68px)">
      <Grid gap={6} display="flex" flexDirection="column" alignItems="center">
        <Typography variant="title1Bold" color="primary">
          {t('bookings.setURLStep.title')}
        </Typography>
        <Typography variant="bodyRegular" color="primary" maxWidth={542} textAlign="center">
          {t('bookings.setURLStep.subTitle')}
        </Typography>
      </Grid>
      <Grid gap={0.5} display="flex" flexDirection="column" m="auto">
        <Typography variant="meta" color="primary">
          {t('bookings.setURLStep.description')}
        </Typography>
        <Grid display="flex">
          <Grid
            bgcolor="rgba(124, 174, 190, 0.50)"
            p="8px 12px"
            borderRadius={isRtl(language) ? '0 10px 10px 0' : '10px 0 0 10px'}
            display="flex"
            alignItems="center"
          >
            <Typography variant="bodyBold" color="primary">
              {isRtl(language)
                ? `/${process.env.REACT_APP_AUTH_SERVICE}/u`
                : `${process.env.REACT_APP_AUTH_SERVICE}/u/`}
            </Typography>
          </Grid>
          <StyledTextField
            isRTL={isRtl(language)}
            error={urlExistStatus === false || !isValidUsername(url, t).isValid}
            value={url}
            onChange={(event) => setUrl(event?.target.value)}
            disabled={!hasPermissions}
          />
        </Grid>
        <Typography variant="bodyRegular" color="error.main">
          {getError()}
        </Typography>
      </Grid>
    </Grid>
  );
}

export default SetURLStep;
